import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import Popper from '@material-ui/core/Popper';
import { BetaButton, Spinner } from '@bevy/bevy-design-system';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MenuDotsVerticalIcon,
} from '@bevy/bevy-design-system/deprecated';

import { useClickBounds } from '@/shared/hooks';
import styles from './styles.module.less';

export default function DropdownArrowMenu({
  menuLabel = <MenuDotsVerticalIcon color='#3C4153' />,
  menuLabelVariant,
  ariaLabel = 'menu dropdown',
  children,
  dataHelpId,
  dataTestId,
  showArrow = true,
  menuPlacement = 'bottom-end',
  menuOffset = '0,9',
  loading,
}) {
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const [arrowRef, setArrowRef] = useState(null);
  const [open, setOpen] = useState(false);

  // Close menu when clicking outside of menu.
  useClickBounds({
    ref: [menuRef, anchorRef],
    callback: () => setOpen(false),
    // Uses default mousedown event.
  });

  // Handle close after clicking menu item.
  useClickBounds({
    ref: menuRef,
    callback: () => setOpen(false),
    outOfBounds: false,
    // Use click event listener,
    // which fires after mousedown (default)
    // and allows item clicks to register first,
    // before executing this callback.
    onEvent: 'click',
  });

  const handleClick = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const indicator = open ? <ChevronUpIcon size='small' /> : <ChevronDownIcon size='small' />;
  return (
    <>
      {menuLabelVariant === 'filled' ? (
        <div ref={anchorRef}>
          <BetaButton
            type='button'
            size='medium'
            spacing='compact'
            ariaLabel={menuLabel}
            onClick={handleClick}
            dataHelpId={dataHelpId}
            dataTestId={dataTestId}
          >
            <span className={styles.menuLabelVariantFilledWrapper}>
              {menuLabel}
              {loading ? (
                <div data-testid='loader'>
                  <Spinner />
                </div>
              ) : (
                indicator
              )}
            </span>
          </BetaButton>
        </div>
      ) : (
        <button
          ref={anchorRef}
          type='button'
          onClick={handleClick}
          className={styles.dropdownBtn}
          aria-describedby='menu-dropdown'
          aria-label={ariaLabel}
          data-help-id={dataHelpId}
          data-testid={dataTestId}
        >
          {menuLabel}
        </button>
      )}
      <Popper
        id='menu-dropdown'
        className={styles.dropdown}
        open={open}
        anchorEl={anchorRef.current}
        placement={menuPlacement}
        transition
        modifiers={{
          arrow: {
            enabled: showArrow,
            element: showArrow && arrowRef,
          },
          offset: {
            offset: menuOffset,
          },
        }}
      >
        <div ref={menuRef} style={{ overflow: 'hidden' }} role='menu'>
          {children}
        </div>
        {showArrow && (
          <span
            data-testid='arrow'
            ref={setArrowRef}
            className={styles.dropdownArrow}
            aria-hidden
          />
        )}
      </Popper>
    </>
  );
}

DropdownArrowMenu.propTypes = {
  menuLabel: PropTypes.node,
  menuLabelVariant: PropTypes.string,
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  dataHelpId: PropTypes.string,
  dataTestId: PropTypes.string,
  showArrow: PropTypes.bool,
  menuPlacement: PropTypes.string,
  menuOffset: PropTypes.string,
  loading: PropTypes.bool,
};
